import type { LookupArtwork } from '../adapters/catalog/artwork';
import { sameTrack, type Track } from '../domain/icy-parse';
import { findStation, type MetaCapability } from '../domain/stations';
import type { NowPlayingPort, StopWatching } from '../ports/metadata';
import { type AppState, playableStations } from './app';
import type { Store } from './index';

/**
 * Življenjska doba spremljanja metapodatkov.
 *
 * Kaskada teče **samo, dokler postaja igra**. Ob pavzi, napaki in menjavi
 * postaje se ustavi in vse povezave v teku se prekinejo — poll, ki teče v
 * ozadju ustavljenega predvajalnika, je promet, ki ga nihče ne bo videl, in
 * pri ICY je to prava povezava s tokom (T3 §3).
 *
 * ⚠️ **Poskus »obdrži skladbo ob pavzi« je umaknjen (2026-07-20).** Hipoteza
 * je bila, da čiščenje `track` ob pavzi sproži nov `MediaMetadata` in ta na
 * zaklenjenem zaslonu Androida podre obvestilo. Preverjeno na napravi:
 * **obvestilo se podre tudi tako.** Vzrok torej ni zamenjava metapodatkov,
 * ampak `audio.pause()` sam, ki pod keyguardom vzame sejo iz krmiljenega
 * stanja v Blinku — omejitev spletne platforme, ne naša napaka (glej HANDOFF).
 */

export interface NowPlayingWatcherDeps {
  port: NowPlayingPort;
  store: Store<AppState>;
  /** Izid kaskade gre v shrambo prek knjižnice (T2 §6). */
  onCapability(stationId: string, capability: MetaCapability): void;
  lookupArtwork: LookupArtwork;
}

export interface NowPlayingWatcher {
  /** Kliče se ob vsaki spremembi stanja; sam ugotovi, ali je kaj za narediti. */
  sync(state: AppState): void;
  stop(): void;
}

export function createNowPlayingWatcher(deps: NowPlayingWatcherDeps): NowPlayingWatcher {
  let watchedId: string | null = null;
  let stopWatching: StopWatching | null = null;

  /**
   * Ovitek pride asinhrono in se sme prikazati **samo, če je skladba še ista**.
   *
   * Poizvedba traja; postaja medtem lahko zamenja skladbo. Brez obeh preverjanj
   * bi se ovitek prejšnje skladbe prilepil k novemu naslovu — napaka, ki je iz
   * kode ni videti, na zaslonu pa je videti kot pravilen podatek.
   */
  async function fetchArtwork(stationId: string, track: Track): Promise<void> {
    const url = await deps.lookupArtwork(track);
    if (url === null) return;
    if (watchedId !== stationId) return;
    if (!sameTrack(deps.store.get().track, track)) return;
    deps.store.set({ artworkUrl: url });
  }

  function halt(): void {
    stopWatching?.();
    stopWatching = null;
    watchedId = null;
  }

  return {
    sync(state) {
      const active = state.playback === 'playing' ? state.stationId : null;
      if (active === watchedId) return;

      stopWatching?.();
      stopWatching = null;
      // ⚠️ `watchedId` se postavi PRED zapisom v store. Zapis sproži naročnike,
      // ti pokličejo `sync()` znova, in če bi bil `watchedId` takrat še star,
      // bi se spremljanje zagnalo dvakrat.
      watchedId = active;

      deps.store.set({ track: null, artworkUrl: null });
      if (active === null) return;

      // `playableStations()`, ne `state.stations`: predposluh iz zaslona
      // Iskanje ni v uporabnikovem seznamu (P2 §2.4), naslov skladbe pa mora
      // imeti tudi on — sicer je videti, kot da metapodatki ne delujejo.
      const station = findStation(playableStations(state), active);
      if (station === null) return;

      stopWatching = deps.port.watch(station, {
        onTrack: ({ track, artworkUrl }) => {
          // Odgovor lahko pride po menjavi postaje: prekinitev povezave ni
          // trenutna in zadnji poll se sme še dokončati.
          if (watchedId !== active) return;
          // Ovitek prejšnje skladbe se umakne TAKOJ. Slika, ki ostane viseti ob
          // novem naslovu, je videti kot pravilen podatek in ni.
          deps.store.set({ track, artworkUrl: artworkUrl ?? null });

          // Ovitek iščemo samo, kadar ga vir ne da sam. AzuraCast pošlje
          // `song.art` iz svoje knjižnice — točnejši od iskanja po imenu in
          // brez enega samega zahtevka navzgor.
          if (track !== null && artworkUrl === undefined) void fetchArtwork(active, track);
        },
        onCapability: (capability) => {
          deps.onCapability(active, capability);
        },
      });
    },

    stop: halt,
  };
}
